import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';
let hasUserInteracted = false;
if (typeof window !== 'undefined') {
  const markInteracted = (event) => {
    if (event && event.isTrusted) {
      hasUserInteracted = true;
    }
  };
  ['pointerdown', 'keydown', 'touchstart', 'mousedown'].forEach((eventType) => {
    window.addEventListener(eventType, markInteracted, { capture: true, passive: true });
  });
}

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement} [panelPill] - The single-brand desktop panel search pill.
 * @property {HTMLInputElement} [panelPillInput] - The visible panel input, synchronized with the header input.
 * @property {HTMLButtonElement} [panelPillReset] - The panel pill reset button.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['predictiveSearchResults'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Synchronizes the current query into the single-brand desktop panel input,
   * which replaces the header pill visually while the dropdown covers it.
   */
  #syncPanelPill = () => {
    const { panelPill, panelPillInput, panelPillReset, searchInput } = this.refs;

    if (!panelPill || !panelPillInput) return;

    const value = searchInput.value ?? '';
    if (panelPillInput.value !== value) panelPillInput.value = value;

    if (panelPillReset) panelPillReset.hidden = false;
  };

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  #initialEmptyMarkup = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.refs.predictiveSearchResults && !this.#initialEmptyMarkup) {
      this.#initialEmptyMarkup = this.refs.predictiveSearchResults.innerHTML;
    }

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput?.value?.length > 0 || this.refs.panelPillInput?.value?.length > 0) {
      this.#showResetButton();
    }

    this.#syncPanelPill();

    // Prevent search bar from being focused automatically on page load
    if (!hasUserInteracted) {
      if (document.activeElement === this.refs.searchInput) {
        this.refs.searchInput.blur();
      }
      this.classList.remove('is-active');
      this.refs.searchInput?.setAttribute('aria-expanded', 'false');
    }

    const handleCloseClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.closeSearch();
    };

    if (this.refs.closeModalButton) {
      this.refs.closeModalButton.addEventListener('click', handleCloseClick, { signal });
    }
    if (this.refs.panelPillReset) {
      this.refs.panelPillReset.addEventListener('click', handleCloseClick, { signal });
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    } else {
      this.refs.searchInput.addEventListener('focus', this.#handleInputFocus, { signal });
      this.refs.searchInput.addEventListener('input', this.#handleInputFocus, { signal });
      this.refs.searchInput.addEventListener('click', this.#handleInputFocus, { signal });
      if (this.refs.panelPillInput) {
        this.refs.panelPillInput.addEventListener('focus', this.#handleInputFocus, { signal });
        this.refs.panelPillInput.addEventListener('input', this.#handleInputFocus, { signal });
        this.refs.panelPillInput.addEventListener('click', this.#handleInputFocus, { signal });
      }
      document.addEventListener('click', this.#handleOutsideClick, { signal });
    }

    this.#initNostoPendingState();
  }

  /**
   * The wrapper div Nosto Search Templates' ContentWrapper.tsx renders once
   * its autocomplete has actually mounted (`nosto_jd_grid` class, always
   * present once `initialized` - loading skeleton, real results, or a
   * genuine no-results state all render inside it; only a still-mounting
   * wrapper is childless). Used to tell "Nosto has content to show" apart
   * from "Nosto's container exists but is still empty".
   * @returns {boolean}
   */
  #hasRenderedNostoContent() {
    const wrapper = this.querySelector('.nosto_jd_grid');
    return !!(wrapper && wrapper.children.length > 0);
  }

  /**
   * @type {number | undefined}
   */
  #nostoPendingFallbackId;

  /**
   * @type {number | undefined}
   */
  #nostoPendingPollId;

  /**
   * Sets up the `data-nosto-pending` loading state for stores where Nosto
   * Search Templates owns this dropdown (see the matching attribute on
   * <predictive-search-component> in snippets/search.liquid, and the CSS
   * that hides/shows content on it in snippets/predictive-search-styles.liquid).
   *
   * Unlike the collection grid's `data-nosto-pending` (cleared by Nosto's own
   * ContentWrapper.tsx, which hardcodes the `results-list` selector), nothing
   * in that bundle manages this dropdown's attribute - so this component
   * does it, continuously, for as long as it's on the page: an observer
   * re-checks on every change inside it, rather than only reacting once at
   * open time, since Nosto's container can empty back out on its own after
   * already having content (the intermittent-empty-dropdown bug this is
   * fixing) with nothing else here to notice that happened.
   */
  #initNostoPendingState() {
    if (this.dataset.searchProvider !== 'nosto') return;

    new MutationObserver(this.#syncNostoPendingState).observe(this, { childList: true, subtree: true });

    // Covers the page-load case: markup rendered server-side already has
    // data-nosto-pending set, but Nosto's autocomplete may not mount until
    // the shopper actually opens the dropdown.
    this.#syncNostoPendingState();
  }

  /**
   * A plain interval poll of #syncNostoPendingState, running only while the
   * dropdown is open, as a backstop alongside the MutationObserver above.
   * Confirmed live: Nosto's own bundle can replace its rendered wrapper with
   * an empty one and never follow up with real content, without that empty
   * swap ever registering as a DOM mutation this element's observer sees
   * (bundled Preact commits can batch multiple DOM operations into a form
   * the observer's callback doesn't reliably catch mid-batch). Polling
   * doesn't depend on catching any specific mutation - it just re-checks
   * what's actually in the DOM on a steady cadence, so it self-corrects
   * within one tick of that happening regardless of why the observer missed it.
   */
  #startNostoPendingPoll() {
    if (this.dataset.searchProvider !== 'nosto') return;
    if (this.#nostoPendingPollId !== undefined) return;
    this.#nostoPendingPollId = window.setInterval(this.#syncNostoPendingState, 500);
  }

  #stopNostoPendingPoll() {
    clearInterval(this.#nostoPendingPollId);
    this.#nostoPendingPollId = undefined;
  }

  /**
   * Reconciles `data-nosto-pending` with whatever is actually in the DOM
   * right now. Three cases:
   *  - Nosto's wrapper has content -> clear pending (and its fallback timer).
   *  - No content, dropdown open -> (re-)arm pending: covers both a fresh
   *    open landing before Nosto has rendered anything yet, AND content
   *    that was there a moment ago disappearing while still open.
   *  - No content, dropdown closed -> leave whatever's already there alone
   *    (nothing to show yet, no need to touch the loading state pre-open).
   * A generous timeout fallback reveals the container regardless, in case
   * Nosto's bundle never renders at all for this open.
   */
  #syncNostoPendingState = () => {
    if (this.dataset.searchProvider !== 'nosto') return;

    if (this.#hasRenderedNostoContent()) {
      this.removeAttribute('data-nosto-pending');
      clearTimeout(this.#nostoPendingFallbackId);
      this.#nostoPendingFallbackId = undefined;
      return;
    }

    if (!this.classList.contains('is-active')) return;

    this.setAttribute('data-nosto-pending', '');

    // A timer is armed here whenever one isn't already running - including
    // the very first open of a page load, where the attribute was already
    // set server-side (so this is a no-op above) but no timer exists yet
    // for it. Guarding on the timer's own existence, not on whether the
    // attribute happens to already be set, is what makes that case get a
    // fallback at all - checking the attribute here would just as easily
    // find it already present at every one of THOSE calls too, since the
    // timeout below hasn't fired yet, and skip arming one forever.
    if (this.#nostoPendingFallbackId === undefined) {
      this.#nostoPendingFallbackId = window.setTimeout(() => {
        this.removeAttribute('data-nosto-pending');
        this.#nostoPendingFallbackId = undefined;
      }, 8000);
    }
  };

  #handleInputFocus = (event) => {
    if (this.isClosing) return;
    hasUserInteracted = true;

    this.classList.add('is-active');
    // After adding `is-active` above, not before: #syncNostoPendingState's
    // "dropdown open" check reads that same class, and this call needs to
    // see the open state to arm the loader for a fresh open where Nosto
    // hasn't rendered anything yet.
    this.#syncNostoPendingState();
    this.#startNostoPendingPoll();

    document.body.classList.add('predictive-search-active');
    document.documentElement.classList.add('predictive-search-active');
    this.refs.searchInput.setAttribute('aria-expanded', 'true');
    this.#syncPanelPill();

    requestAnimationFrame(() => {
      if (this.isClosing) return;
      this.#focusActiveSearchInput();
    });
  };

  /**
   * Returns focus to the visible input when the single-brand panel is open.
   * Other predictive-search variants continue to use the header input.
   */
  #focusActiveSearchInput = () => {
    const { panelPillInput, searchInput } = this.refs;
    // `preventScroll` is required here: the header search input can be
    // scrolled down past the viewport at the moment this focus() call
    // happens (`snippets/search.liquid`'s click handler sets it sticky
    // asynchronously — see that file's own comment — so there's a real
    // window where the header hasn't visually pinned to the top yet).
    // Without `preventScroll`, the browser's default "scroll the newly
    // focused element into view" behavior takes over and jumps the whole
    // page to the input's natural (unstickied) position, which reads as
    // "the page scrolled to the top" and, mid-transition, a half-visible
    // header. Confirmed live: scrolling down, then opening the dropdown
    // without first scrolling up (so the sticky header was never revealed)
    // reproduced this exactly.
    (panelPillInput ?? searchInput)?.focus({ preventScroll: true });
  };

  /**
   * Syncs typing in the visible single-brand desktop panel input to the header
   * input, then reuses the existing predictive-search request flow.
   */
  syncPanelPillInput(event) {
    hasUserInteracted = true;
    const panelInput = /** @type {HTMLInputElement} */ (event.target);
    this.refs.searchInput.value = panelInput.value;
    // Setting `.value` directly does NOT fire a native `input` event on
    // `searchInput` — the Nosto Search Templates bundle (a separate
    // project, not this file) attaches its own `input` listener to this
    // exact element to drive its autocomplete state, the same way
    // `#getSearchResults` above defers to it entirely on
    // `data-search-provider="nosto"` stores. Without dispatching this,
    // typing into the single-brand header style's visible panel pill
    // silently updated `searchInput.value` but Nosto's autocomplete never
    // saw it — confirmed live: no results appeared no matter what was
    // typed here, even though the plain (multi-brand) header input worked
    // fine, since that one fires real `input` events by just being typed
    // into directly.
    this.refs.searchInput.dispatchEvent(new Event('input', { bubbles: true }));
    this.#handleInputFocus(event);
    this.search(event);
  }

  #handleOutsideClick = (event) => {
    if (!this.contains(event.target)) {
      // Only touch THIS instance's own state here. The theme renders
      // several simultaneous <predictive-search-component> instances
      // (desktop bar, mobile bar, drawer variant), each with its own
      // #handleOutsideClick bound to `document` - a click inside ANY ONE
      // of them is "outside" every OTHER instance, so all of their outside
      // handlers fire for that same click. Directly stripping the shared
      // `predictive-search-active` class from <body>/<html> here used to
      // blow away state another (genuinely still-open) instance had just
      // set moments earlier - confirmed live via MutationObserver logging:
      // clicking the desktop search pill added `is-active` + the shared
      // active classes, then within ~100ms an inactive sibling instance's
      // own outside-click handler for that same click removed the shared
      // classes again, producing a visible open/close flicker even though
      // the clicked instance's own dropdown never actually closed.
      // `snippets/search.liquid`'s own MutationObserver
      // (initPredictiveSearchActiveObserver) already watches `class`/`open`
      // changes across the whole document and is the single source of
      // truth for whether the shared classes + scroll lock should be
      // applied - removing `is-active` here is enough for it to correctly
      // recompute that global state (it only clears the shared classes
      // when NO instance/dialog is left active).
      this.classList.remove('is-active');
      this.refs.searchInput.setAttribute('aria-expanded', 'false');
      this.#stopNostoPendingPoll();
    }
  };

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus({ preventScroll: true });
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    clearTimeout(this.#nostoPendingFallbackId);
    this.#stopNostoPendingPoll();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
        '.predictive-search-results__wrapper-products, ' +
        '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.#focusActiveSearchInput();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      if (!this.dialog) {
        this.refs.searchInput.blur();
      }
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else if (!event.isComposing) {
          // Submit the form, the same single path the search button takes:
          // Nosto Search Templates handles the submit event on Nosto stores,
          // the browser submits natively otherwise. Navigating here as well
          // used to start a second navigation on Nosto stores, and that one
          // could carry a different (stale) query. Confirmed live: Enter on
          // "sleeping bag" requested both /search?q=sleeping+bag and
          // /search?q=sleeping. A keydown while an IME is still composing
          // is left alone so the keyboard can commit its text first.
          event.preventDefault();
          const form = this.refs.searchInput.form;
          if (typeof form?.requestSubmit === 'function') {
            form.requestSubmit();
          } else {
            form?.submit();
          }
        }
        break;
      }
    }
  };

  handleClearOrClose = (event) => {
    this.closeSearch(event);
  };

  closeSearch = (event) => {
    if (event) {
      event.preventDefault?.();
      event.stopPropagation?.();
    }
    this.isClosing = true;
    this.classList.remove('is-active');
    this.#stopNostoPendingPoll();
    document.body.classList.remove('predictive-search-active', 'search-modal-open');
    document.documentElement.classList.remove('predictive-search-active', 'search-modal-open');
    document.documentElement.removeAttribute('data-search-modal-open');
    document.body.removeAttribute('data-search-modal-open');
    // `window.themeSearchScrollLock` (snippets/search.liquid) is the single
    // authoritative implementation of the background scroll lock - it
    // restores the exact scroll offset it captured on open, which a plain
    // style reset here can't do, and keeps its own "currently locked" flag
    // in sync so the NEXT open still applies the lock (a direct style
    // reset here previously left that flag stuck "locked" after the first
    // close routed through this function, silently breaking the lock on
    // every open after that - confirmed live on a real iPhone).
    window.themeSearchScrollLock?.unlock();

    if (this.refs.searchInput) {
      this.refs.searchInput.setAttribute('aria-expanded', 'false');
      this.refs.searchInput.blur();
    }
    if (this.refs.panelPillInput) {
      this.refs.panelPillInput.blur();
    }

    this.#resetSearch();

    const modal = document.getElementById('search-modal');
    if (modal && typeof modal.closeDialog === 'function') {
      modal.closeDialog();
    }
    if (this.dialog && typeof this.dialog.closeDialog === 'function') {
      this.dialog.closeDialog();
    }

    setTimeout(() => {
      this.classList.remove('is-active');
      document.body.classList.remove('predictive-search-active', 'search-modal-open');
      document.documentElement.classList.remove('predictive-search-active', 'search-modal-open');
      document.documentElement.removeAttribute('data-search-modal-open');
      document.body.removeAttribute('data-search-modal-open');
      // Idempotent - `lock`'s/`unlock`'s own "already (un)locked" guards
      // make a second call here (after the synchronous one above) a no-op
      // when nothing reopened the drawer in between; harmless either way.
      window.themeSearchScrollLock?.unlock();
      this.isClosing = false;
    }, 300);
  };

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus === true) {
      this.#focusActiveSearchInput();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    const activeInput = (this.refs.panelPillInput && this.refs.panelPillInput.value.length > 0)
      ? this.refs.panelPillInput
      : this.refs.searchInput;

    const searchTerm = (activeInput?.value ?? this.refs.searchInput?.value ?? '').trim();

    this.#currentIndex = -1;

    if (!searchTerm.length) {
      // When input is emptied (backspace), show the default empty state but
      // keep the drawer open — only the X button / Escape / overlay click should close it.
      this.#showEmptyState();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results and update the results container. When Nosto owns
   * this dropdown (`data-search-provider="nosto"`), this is a no-op —
   * Nosto Search Templates (nosto-search-templates project) attaches its
   * own `input`/`focus` listeners to this exact same input element and
   * independently fetches + portals its own results in; both this native
   * morph() AND Search Templates writing into the same
   * `.predictive-search-form__content` container on every keystroke would
   * otherwise race each other. Otherwise, Shopify's native predictive
   * search.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;
    if (this.dataset.searchProvider === 'nosto') return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[type]', 'product,collection,article,query');
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    const resultsMarkup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
    if (!resultsMarkup || abortController.signal.aborted) return;

    morph(predictiveSearchResults, resultsMarkup);

    this.#resetScrollPositions();
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    if (resetButton) resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton, panelPillReset } = this.refs;

    if (resetButton) resetButton.hidden = false;
    if (panelPillReset) panelPillReset.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Shows the default empty/initial state (Popular Categories etc.) without
   * closing the drawer. Called when the user backspaces the input to empty.
   */
  #showEmptyState = async () => {
    // DISABLED when Nosto Search Templates owns this dropdown — see the
    // identical guard + comment in #getSearchResults above.
    if (this.dataset.searchProvider === 'nosto') return;
    
    const { predictiveSearchResults } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    this.#hideResetButton();
    this.#syncPanelPill();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    try {
      const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
      const parsedEmptySectionMarkup = new DOMParser()
        .parseFromString(emptySectionMarkup, 'text/html')
        .querySelector('.predictive-search-empty-section');

      if (parsedEmptySectionMarkup && predictiveSearchResults) {
        if (abortController.signal.aborted) return;
        morph(predictiveSearchResults, parsedEmptySectionMarkup);
        this.#resetScrollPositions();
      }
    } catch (e) {
      // Ignore empty section fetch error
    }
  };

  /**
   * Fully resets the search and closes the drawer.
   * Called explicitly by Escape key, X (reset) button, or outside-click.
   *
   * When Nosto owns this dropdown, this intentionally does much less than
   * the native path below: clearing the input value and walking
   * #currentIndex's aria-selected bookkeeping over
   * `.predictive-search-results__card` elements - a class Nosto's own
   * autocomplete cards also carry, to visually match native results (see
   * DynamicCardProduct.tsx in nosto-search-templates) - both touch DOM
   * Nosto's bundle owns. Confirmed live: going through the full reset here
   * left Nosto's rendered autocomplete empty on the next open, needing a
   * full multi-second re-render, where the plain overlay/outside-click path
   * (skips this function entirely) preserved it instantly. Only the
   * genuinely theme-owned bits - hiding the reset button, closing the
   * drawer itself - run for Nosto; the rest is left to Nosto's own bundle.
   */
  #resetSearch = async () => {
    const { searchInput, panelPillInput } = this.refs;
    const isNosto = this.dataset.searchProvider === 'nosto';

    if (!isNosto) {
      this.#currentIndex = -1;
      if (searchInput) searchInput.value = '';
      if (panelPillInput) panelPillInput.value = '';
    } else {
      // The input's VALUE still has to be cleared for Nosto too - leaving
      // it untouched (the previous behavior) meant clicking the X/reset
      // button visibly closed the drawer but left the typed text sitting
      // in the input, so reopening showed the same stale query instead of
      // a blank search bar. Confirmed live: this is distinct from the
      // "don't reset Nosto's rendered DOM" concern the surrounding isNosto
      // guard exists for (see class comment above) - clearing `.value`
      // and re-syncing the panel pill doesn't touch anything Nosto's own
      // bundle rendered, only the plain form input's own state.
      // A bare `.value = ''` fires no native `input` event (same gap
      // documented on syncPanelPillInput above), so the Nosto Search
      // Templates bundle's own `onInput` listener on this exact element
      // never sees the change and keeps its last typed query in its own
      // React state - dispatching one here lets it clear that state too,
      // so the NEXT open shows its fresh/empty view rather than stale
      // results for a query the visible input no longer shows.
      if (searchInput) {
        searchInput.value = '';
        searchInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
      if (panelPillInput) panelPillInput.value = '';
    }

    this.#hideResetButton();
    this.#syncPanelPill();

    if (!this.dialog) {
      this.classList.remove('is-active');
      if (searchInput) searchInput.setAttribute('aria-expanded', 'false');
    }

    if (isNosto) return;

    // Load empty section after closing so next open shows fresh state
    await this.#showEmptyState();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
